import { print } from 'graphql';
import { StatusCodes } from 'http-status-codes';
import { ApolloLink, HttpLink, ErrorLike } from '@apollo/client';
import { PersistedQueryLink } from '@apollo/client/link/persisted-queries';
// The Apollo 4 release candidate declares ESM but its main entry is UMD.
// Use its ESM entry so the production Node server can load it.
import { RestLink } from 'apollo-link-rest/index.js';
import { delay } from 'rxjs';
import fetch from 'isomorphic-fetch';
import sha from 'sha.js';
import { deepPropertyCount } from '@moda/portal-stanchions';
import { CONFIG } from '../../../config';
import { getGraphQLErrors, getGraphQLErrorCode } from '../../error';
import { setRestHeaders } from '../setRestHeaders';
import { USER_API_URL } from '../../../constants';

export const isUnauthorizedError = (error: ErrorLike) =>
  getGraphQLErrors(error)?.some(error => getGraphQLErrorCode(error) === 'unauthorized') ||
  ('statusCode' in error && error.statusCode === StatusCodes.UNAUTHORIZED);

const persistedQueriesCount = Number(CONFIG.PERSISTED_QUERIES_MAX_VARIABLES_COUNT);
const defaultPersistedQueriesCount = 100;

const PERSISTED_QUERIES_MAX_VARIABLES_COUNT = isNaN(persistedQueriesCount)
  ? defaultPersistedQueriesCount
  : persistedQueriesCount;

export const createCommonConfiguration = ({
  additionalRestHeaders = {}
}: {
  additionalRestHeaders?: { [key: string]: string };
}) => {
  const restLink = new RestLink({
    customFetch: fetch,
    endpoints: {
      moda: CONFIG.MODA_API_URL,
      preferences: CONFIG.PREFERENCES_API_URL,
      recommendations: CONFIG.RECOMMENDATIONS_API_URL,
      user: USER_API_URL,
      self: CONFIG.DISCOVERY_API_URL // TODO: try to figure out if there's a way to use a relative path
    },
    uri: CONFIG.MODA_API_URL,
    headers: {
      ...additionalRestHeaders,
      'Content-Type': 'application/vnd.api+json'
    },
    fieldNameNormalizer: name => name.replace(/[^a-zA-Z0-9_]/gi, '')
  });

  return {
    link: ApolloLink.from([
      setRestHeaders('recommendations', { 'x-api-key': 'abracadabra' }),
      // RestLink emits a JSON API error body before its transport error. Defer
      // results so the HTTP error wins, retaining its status and field details.
      new ApolloLink((operation, forward) => restLink.request(operation, forward).pipe(delay(0))),
      new ApolloLink((operation, forward) => forward(operation)).split(
        operation =>
          deepPropertyCount(operation.variables) <= PERSISTED_QUERIES_MAX_VARIABLES_COUNT,
        new PersistedQueryLink({
          generateHash: node => sha('sha256').update(print(node)).digest('hex'),
          useGETForHashedQueries: true
        }).concat(
          new HttpLink({
            uri: CONFIG.SEARCH_API_GRAPHQL_ENDPOINT,
            fetch
          })
        ),
        new HttpLink({
          uri: CONFIG.SEARCH_API_GRAPHQL_ENDPOINT,
          fetch
        })
      )
    ])
  };
};
